import { ScrollReveal } from '@/hooks/useScrollReveal';
import { Palette, Layout, Type, Layers } from 'lucide-react';
import { useTilt } from '@/hooks/useEffects';

const STYLES = [
  {
    icon: Layout,
    title: 'Clean & Minimal',
    tagline: 'Less noise. More impact.',
    description: 'Elegant whitespace, sharp typography, and a focus on content that lets your business speak for itself.',
    preview: {
      bg: 'bg-gradient-to-br from-white to-slate-50 dark:from-[#151515] dark:to-[#1a1a1a]',
      blocks: ['bg-slate-900', 'bg-slate-100 dark:bg-slate-800', 'bg-slate-100 dark:bg-slate-800', 'bg-slate-100 dark:bg-slate-800'],
    },
    span: 'md:col-span-7',
  },
  {
    icon: Palette,
    title: 'Bold & Modern',
    tagline: 'Stand out. Get noticed.',
    description: 'Strong colours, dynamic layouts, and eye-catching design that grabs attention instantly.',
    preview: {
      bg: 'bg-gradient-to-br from-slate-900 to-slate-800',
      blocks: ['bg-[#00E5FF]', 'bg-slate-700', 'bg-slate-700', 'bg-slate-700'],
    },
    span: 'md:col-span-5',
  },
  {
    icon: Type,
    title: 'Elegant & Classic',
    tagline: 'Timeless. Refined. Premium.',
    description: 'Refined aesthetics with serif touches, warm tones, and a sophisticated feel perfect for premium brands.',
    preview: {
      bg: 'bg-gradient-to-b from-stone-50 to-amber-50/30 dark:from-stone-900 dark:to-stone-800',
      blocks: ['bg-stone-700', 'bg-stone-200 dark:bg-stone-600', 'bg-stone-200 dark:bg-stone-600', 'bg-amber-100 dark:bg-amber-900'],
    },
    span: 'md:col-span-5',
  },
  {
    icon: Layers,
    title: 'Dynamic & Interactive',
    tagline: 'Engage. Explore. Convert.',
    description: 'Animated elements, smooth transitions, and an engaging experience that keeps visitors exploring.',
    preview: {
      bg: 'bg-gradient-to-br from-indigo-950 to-violet-900',
      blocks: ['bg-cyan-400', 'bg-indigo-800', 'bg-indigo-800', 'bg-violet-700'],
    },
    span: 'md:col-span-7',
  },
];

function MiniPreview({ preview }) {
  return (
    <div className="rounded-xl overflow-hidden border border-slate-200/40 dark:border-white/10 shadow-sm mt-6 group-hover:shadow-md transition-shadow">
      <div className="flex items-center gap-1.5 px-3 py-2 bg-white/50 dark:bg-black/30 border-b border-slate-200/30 dark:border-white/5">
        <div className="w-2 h-2 rounded-full bg-rose-300" />
        <div className="w-2 h-2 rounded-full bg-amber-300" />
        <div className="w-2 h-2 rounded-full bg-emerald-300" />
      </div>
      <div className={`p-4 ${preview.bg} space-y-3`}>
        <div className={`h-10 ${preview.blocks[0]} rounded-lg opacity-90`} />
        <div className="flex gap-2">
          {preview.blocks.slice(1).map((b, i) => (
            <div key={i} className={`h-6 flex-1 ${b} rounded`} />
          ))}
        </div>
        <div className={`h-2 ${preview.blocks[0]} rounded-full w-2/5 opacity-40`} />
      </div>
    </div>
  );
}

function StyleCard({ style, i }) {
  const tilt = useTilt(3);
  return (
    <ScrollReveal delay={i * 100} className={style.span}>
      <div
        data-testid={`portfolio-card-${i}`}
        ref={tilt.ref}
        onMouseMove={tilt.onMouseMove}
        onMouseLeave={tilt.onMouseLeave}
        className="tilt-card group relative rounded-2xl overflow-hidden bg-white dark:bg-[#111111] border border-slate-200/60 dark:border-white/10 p-8 md:p-10 h-full glow-hover"
      >
        <div className="absolute top-0 left-0 w-full h-[2px] shimmer-border" />
        <div className="flex items-start gap-4 mb-1">
          <div className="w-10 h-10 rounded-lg bg-[#F8F9FA] dark:bg-white/5 border border-slate-100 dark:border-white/5 flex items-center justify-center group-hover:bg-[#00E5FF]/10 group-hover:border-[#00E5FF]/20 transition-colors flex-shrink-0">
            <style.icon className="w-4 h-4 text-slate-500 dark:text-slate-400 group-hover:text-[#00B4D8] transition-colors" />
          </div>
          <div className="flex-1">
            <h3 className="font-['Outfit'] text-xl sm:text-2xl font-bold tracking-tight text-[#0A0A0A] dark:text-white mb-0.5">
              {style.title}
            </h3>
            <p className="text-xs font-semibold uppercase tracking-widest text-[#00B4D8]">
              {style.tagline}
            </p>
          </div>
        </div>
        <p className="text-sm leading-relaxed text-slate-500 dark:text-slate-400 mt-3">{style.description}</p>
        <MiniPreview preview={style.preview} />
      </div>
    </ScrollReveal>
  );
}

export default function PortfolioSection() {
  return (
    <section id="portfolio" data-testid="portfolio-section" className="py-24 md:py-32 relative">
      <div className="absolute top-0 left-1/2 -translate-x-1/2 gradient-line w-1/3" />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <ScrollReveal>
          <span className="text-xs sm:text-sm font-bold uppercase tracking-[0.2em] text-slate-400 block mb-4">
            Design Styles
          </span>
          <h2 className="font-['Outfit'] text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-[#0A0A0A] dark:text-white mb-4 max-w-lg">
            What Your Website Could Look Like
          </h2>
          <p className="text-base sm:text-lg leading-relaxed text-slate-600 dark:text-slate-400 mb-16 max-w-lg">
            Every business is unique. I craft a design direction that fits your brand perfectly.
          </p>
        </ScrollReveal>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-8">
          {STYLES.map((style, i) => (
            <StyleCard key={style.title} style={style} i={i} />
          ))}
        </div>
      </div>
    </section>
  );
}
